import React, { useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Select, Modal, Spinner, Badge } from '../ui.jsx';

// Bulk tech invite wizard. Owner types name + phone for each tech (up to 8 at
// once), clicks Send — every tech gets a 1-tap SMS with a magic link to set up
// their phone for the tech app. Removes the biggest tech-adoption blocker
// (manual signup + remembering passwords).

export function TechInviteWizard({ open, onClose, onDone, toast }) {
  const [rows, setRows] = useState([
    { name: '', phone: '', role: 'tech' },
    { name: '', phone: '', role: 'tech' },
    { name: '', phone: '', role: 'tech' },
  ]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  if (!open) return null;

  const update = (i, patch) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    setRows(next);
  };
  const addRow = () => rows.length < 8 && setRows([...rows, { name: '', phone: '', role: 'tech' }]);

  const send = async () => {
    const invites = rows
      .map(r => ({ name: r.name.trim(), phone: r.phone.replace(/[^\d+]/g, ''), role: r.role }))
      .filter(r => r.name && r.phone);
    if (!invites.length) return toast?.('Add at least one name + phone', 'warn');
    setBusy(true);
    try {
      const r = await api('/api/onboarding/tech-invites', { method: 'POST', body: { invites } });
      setResults(r);
      const failures = (r.results || []).filter(x => !x.ok).length;
      if (!failures) toast?.(`✓ Sent ${r.sent} invite${r.sent===1?'':'s'} — texts on their phones now`);
      else toast?.(`Sent ${r.sent} · ${failures} failed (see details)`, 'warn');
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={620}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
      <span style={{ fontSize:22 }}>👥</span>
      <h2 style={{ margin:0, fontSize:20 }}>Invite your team</h2>
    </div>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>
      Each person gets a 1-tap text with a magic link. They open it on their phone, install the tech app, log in — done. No passwords, no QR codes, no IT calls.
    </div>

    {!results && <>
      {rows.map((r, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'1.2fr 1.2fr 100px 24px', gap:8, marginBottom:8, alignItems:'center' }}>
          <Input value={r.name} onChange={e=>update(i, { name:e.target.value })} placeholder={i===0 ? 'First Last' : 'Name'}/>
          <Input type="tel" value={r.phone} onChange={e=>update(i, { phone:e.target.value })} placeholder="(555) 123-4567"/>
          <Select value={r.role} onChange={e=>update(i, { role:e.target.value })}>
            <option value="tech">Tech</option>
            <option value="dispatcher">Dispatcher</option>
            <option value="admin">Admin</option>
          </Select>
          {rows.length > 1 && <span onClick={()=>setRows(rows.filter((_,j)=>j!==i))} title="Remove"
            style={{ cursor:'pointer', color:G.textMuted, fontSize:16, textAlign:'center' }}>✕</span>}
        </div>
      ))}
      {rows.length < 8 && <Btn sz="sm" v="ghost" onClick={addRow} style={{ marginTop:4 }}>+ Add another</Btn>}

      <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:18 }}>
        <Btn onClick={onClose}>Skip</Btn>
        <Btn v="primary" onClick={send} disabled={busy}>{busy ? <Spinner sz={14}/> : '📲 Send invites'}</Btn>
      </div>
    </>}

    {results && <>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:10 }}>
        <strong style={{ color:G.text }}>{results.sent}</strong> invite{results.sent===1?'':'s'} sent · they'll receive a text in the next 30 seconds.
      </div>
      <div style={{ ...styles.card, padding:0, marginBottom:14, maxHeight:280, overflow:'auto' }}>
        {(results.results || []).map((r, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:`1px solid ${G.cardBorder}`, fontSize:13 }}>
            <div>
              <strong>{r.name || '(no name)'}</strong>
              <span style={{ color:G.textMuted, marginLeft:8, fontSize:12 }}>{r.phone}</span>
            </div>
            {r.ok ? <Badge c={G.green} bg={G.greenLight}>✓ Sent</Badge> : <Badge c={G.red} bg={G.redLight} title={r.error}>✕ {r.error}</Badge>}
          </div>
        ))}
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
        <Btn v="primary" onClick={()=>{ onDone?.(); onClose(); }}>Done</Btn>
      </div>
    </>}
  </Modal>;
}
